import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SocialAccountType } from '@anytag/graphql';
import { useDeepCompareEffect } from '@anytag/shared-hooks';
import { SelectedSocialMediaAccountsType } from '@anytag/shared-organisms';
import { formatIntNumber } from '@anytag/shared-utils';
import { useSelectInfluencersMapper } from './use-select-influencers-mapper';
import { useToggleSelectedSocialAccounts } from './use-toggle-selected-social-accounts';

interface Props {
  socialMedia: keyof typeof SocialAccountType;
}

export const useSocialAccountSelectButtonProps = <
  Profile extends Record<string, any> & {
    id: number | string;
    socialAccountId?: number | string | null;
    socialAccountType: SocialAccountType;
  }
>({
  socialMedia
}: Props) => {
  const { t } = useTranslation();
  const { toggleSocialMediaSelectedInfluencer } = useToggleSelectedSocialAccounts();
  const { selectedInfluencersMapIds } = useSelectInfluencersMapper();
  const [togglingState, setTogglingState] = useState<{ id: number | null; loading: boolean }>({
    id: null,
    loading: false
  });

  const resetLoadingState = () => {
    setTogglingState({ id: null, loading: false });
  };

  useDeepCompareEffect(() => {
    resetLoadingState();
  }, [selectedInfluencersMapIds]);

  const getSortSelectButtonProps = (listRecords: readonly Profile[], totalRecords: number) => {
    const allInfluencerIdsInPage = listRecords.map((infl) => Number(infl.id));
    const { selectedIds, selectedSocialMediaAccounts } = listRecords.reduce<{
      selectedIds: number[];
      selectedSocialMediaAccounts: SelectedSocialMediaAccountsType[];
    }>(
      (rs, infl) => {
        const inflId = Number(infl.id);
        const socialAccountId = Number(infl.socialAccountId);

        if (
          selectedInfluencersMapIds.get(inflId)?.get(socialMedia)?.includes(socialAccountId) &&
          !rs.selectedIds.includes(socialAccountId)
        ) {
          rs.selectedIds.push(socialAccountId);
          rs.selectedSocialMediaAccounts.push({
            socialAccountId: infl.socialAccountId,
            socialAccountType: infl.socialAccountType,
            influencerId: inflId
          });
        }
        return rs;
      },
      { selectedIds: [], selectedSocialMediaAccounts: [] }
    );
    const listRecordsLength = listRecords.length;
    const loading = !togglingState.id && togglingState.loading;
    const numOfSelectedAccountsInPage = selectedIds.length;
    const hasAtLeastOneAccountSelectedInPage = numOfSelectedAccountsInPage > 0;
    const hasAllAccountsSelectedInPage = listRecordsLength > 0 && selectedIds.length >= listRecordsLength;

    return {
      loading,
      hasAllSelected: hasAllAccountsSelectedInPage,
      indeterminate: numOfSelectedAccountsInPage !== listRecordsLength,
      selectedSocialMediaAccounts,
      checked: hasAtLeastOneAccountSelectedInPage,
      onClick: async () => {
        setTogglingState({ id: null, loading: true });
        const { error } = await toggleSocialMediaSelectedInfluencer({
          influencerIds: allInfluencerIdsInPage,
          isChecked: hasAllAccountsSelectedInPage,
          socialAccountMedia: SocialAccountType[socialMedia],
          socialAccountIds: listRecords.map((i) => Number(i.socialAccountId)) || []
        });
        if (error) {
          resetLoadingState();
        }
      },
      disabled: loading || listRecords.length === 0,
      label: hasAtLeastOneAccountSelectedInPage
        ? t('numberInfluencersAddedToProposal', { count: numOfSelectedAccountsInPage })
        : t('Accounts found', { count: formatIntNumber(totalRecords) as any })
    };
  };

  const getListSelectButtonProps = (profile: Profile) => {
    const id = Number(profile.id);
    const socialAccountId = Number(profile.socialAccountId);
    const socialAccountMedia = SocialAccountType[socialMedia];
    const loading = togglingState.id === profile.socialAccountId && togglingState.loading;
    const isSelected = selectedInfluencersMapIds?.get(id)?.get(socialAccountMedia)?.includes(socialAccountId) || false;

    return {
      loading,
      checked: isSelected,
      disabled: loading,
      onClick: async () => {
        setTogglingState({ id: Number(profile.socialAccountId), loading: true });
        const { error } = await toggleSocialMediaSelectedInfluencer({
          socialAccountMedia,
          influencerIds: [id],
          isChecked: isSelected,
          socialAccountIds: [socialAccountId]
        });
        if (error) {
          resetLoadingState();
        }
      }
    };
  };

  return {
    getSortSelectButtonProps,
    getListSelectButtonProps
  };
};
